/**
 * Admin-controlled backfill window for staff attendance.
 *
 * Master Admin only. Lets the user open / close / adjust the date range
 * during which staff can mark past attendance and OT from "My Attendance".
 *
 * When `active=false`, staff can only mark TODAY's entry.
 * When `active=true` and date range set, staff can mark any date within.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Save, Lock, Unlock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls, todayISO } from '../billing/shared';

const BackfillWindowCard = () => {
  const { authHeader, user } = useAuth();
  const isMaster = !!user?.is_master_admin;
  const [win, setWin] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/attendance/backfill-window`, { headers: authHeader() });
      setWin({
        active: !!data?.active,
        date_from: data?.date_from || '',
        date_to: data?.date_to || todayISO(),
      });
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/attendance/backfill-window`,
        payload,
        { headers: authHeader() },
      );
      setWin({
        active: !!data.active,
        date_from: data.date_from || '',
        date_to: data.date_to || '',
      });
      toast.success(data.active ? 'Backfill window opened.' : 'Backfill window closed.');
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const onSave = () => {
    if (!win.date_from || !win.date_to) {
      toast.error('Both dates are required to open the window.');
      return;
    }
    if (win.date_from > win.date_to) {
      toast.error('Start date must be on or before end date.');
      return;
    }
    save({ date_from: win.date_from, date_to: win.date_to, active: true });
  };

  const onClose = () => {
    if (!window.confirm('Close the backfill window? Staff will no longer be able to mark past attendance.')) return;
    save({ active: false, date_from: win.date_from || '', date_to: win.date_to || '' });
  };

  if (!isMaster) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4" data-testid="backfill-card">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
            {win?.active ? <Unlock size={14} className="text-emerald-600" /> : <Lock size={14} className="text-slate-400" />}
            Attendance Backfill Window
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Open a date range so staff can mark past attendance &amp; OT from their &ldquo;My Attendance&rdquo; page.
            Outside this window, staff can only mark today.
          </p>
        </div>
        {win?.active && (
          <span className="inline-flex items-center text-[10px] uppercase font-bold tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-300 rounded-full px-2 py-0.5" data-testid="backfill-status-open">
            Open
          </span>
        )}
        {win && !win.active && (
          <span className="inline-flex items-center text-[10px] uppercase font-bold tracking-wider bg-slate-100 text-slate-500 border border-slate-300 rounded-full px-2 py-0.5" data-testid="backfill-status-closed">
            Closed
          </span>
        )}
      </div>

      {loading || !win ? (
        <div className="py-4 text-center text-slate-400 text-sm"><Loader2 className="inline animate-spin mr-1" size={12} /> Loading…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-2 items-end">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">From</label>
            <input
              type="date"
              value={win.date_from}
              max={win.date_to || todayISO()}
              onChange={(e) => setWin({ ...win, date_from: e.target.value })}
              className={inputCls}
              data-testid="backfill-from"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">To</label>
            <input
              type="date"
              value={win.date_to}
              min={win.date_from}
              max={todayISO()}
              onChange={(e) => setWin({ ...win, date_to: e.target.value })}
              className={inputCls}
              data-testid="backfill-to"
            />
          </div>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-semibold px-3 py-2 rounded-lg"
            data-testid="backfill-open"
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            {win.active ? 'Update' : 'Open Window'}
          </button>
          {win.active && (
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 disabled:bg-slate-100 disabled:cursor-not-allowed text-slate-700 text-sm font-semibold px-3 py-2 rounded-lg"
              data-testid="backfill-close"
            >
              <Lock size={14} /> Close
            </button>
          )}
        </div>
      )}

      {win?.active && (
        <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 flex items-start gap-1.5" data-testid="backfill-active-banner">
          <AlertCircle size={12} className="mt-0.5 text-amber-600 shrink-0" />
          <span>
            Staff can mark attendance &amp; OT for any date between <b>{win.date_from}</b> and <b>{win.date_to}</b>.
            Future dates are always blocked. Remember to close the window once everyone has caught up.
          </span>
        </div>
      )}
    </div>
  );
};

export default BackfillWindowCard;
